import React, { FunctionComponent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faEnvelope, faMobile } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import Button from 'components/UI/V3/Button/Button';
import { showErrorThunk } from '@spike/notifications-action';
import { sendFormsThunk, FormsStatus } from '@spike/forms-action';
import useNonInitialEffect from '@versiondos/hooks';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import Client from '@spike/client-model';
import styles from './SendForms.module.scss';
import Channel from 'components/Forms/ui/Channel';
import SelectMultipleDropdown, { SelectDropdownOption } from 'components/Forms/ShareSendClients/SelectMultipleDropdown';
import MobileCloseButton from 'components/Forms/ui/MobileCloseButton';
import { formatPhoneNumber } from '@spike/phone-utils';
import AlertMessage from 'components/UI/V3/AlertMessage';

interface SendFormsProps {
    client: Client;
    onClose: () => void;
    initialFormId?: number;
    onSent?: () => void;
    onNavigateToClientProfile?: () => void;
}

const SendForms: FunctionComponent<SendFormsProps> = ({
    client,
    onClose,
    initialFormId,
    onSent,
    onNavigateToClientProfile
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();

    const formsSlice = useSelector((s: RootState) => (s as any).forms);
    const allForms = formsSlice?.forms || [];
    const formsStatus = useSelector<RootState, FormsStatus>(s => s.forms.status);

    // Build options for SelectDropdown
    const formOptions: Array<SelectDropdownOption<number>> = useMemo(() => {
        return allForms.map((f: any) => ({
            id: f.id as number,
            label: f.title || f.name || `Form ${f.id}`
        }));
    }, [allForms]);

    const [selectedForms, setSelectedForms] = useState<Array<SelectDropdownOption<number>>>([]);
    const [selectedChannel, setSelectedChannel] = useState<'email' | 'sms'>('email');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    // If an initialFormId is provided, preselect it when options are available
    useEffect(() => {
        if (!initialFormId) return;
        if (!formOptions || formOptions.length === 0) return;

        const match = formOptions.find(o => o.id === initialFormId);
        if (match) {
            setSelectedForms([match]);
        }
    }, [initialFormId, formOptions]);

    // Client info derived from props
    const clientName = useMemo(() => {
        const firstName = client.firstName || (client as any).first_name || '';
        const lastName = client.lastName || (client as any).last_name || '';
        return `${firstName} ${lastName}`.trim() || 'Unknown Client';
    }, [client]);

    const clientEmail = client.email || '';
    const clientPhone = client.phone || '';

    const isEmailMissing = selectedChannel === 'email' && !client.email;
    const isPhoneMissing = selectedChannel === 'sms' && !client.phone;
    const showMissingAlert = isEmailMissing || isPhoneMissing;

    const missingLabel = isEmailMissing ? 'email' : 'phone number';
    const missingSuffix = isEmailMissing ? 'via email' : 'via SMS';

    const handleClientProfileLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
        event.stopPropagation();
        event.preventDefault();
        onNavigateToClientProfile?.();
        onClose();
    };

    const handleSend = () => {
        if (selectedForms.length === 0) return;

        const payload = {
            sentVia: selectedChannel as 'email' | 'sms',
            formIds: selectedForms.map(f => String(f.id)),
            customerIds: [String(client.id)]
        };

        dispatch(sendFormsThunk(apiClientWrapper as any, payload as any) as any);
    };

    const resetState = () => {
        setSelectedForms([]);
        setSent(false);
    };

    const handleDone = () => {
        resetState();
        try {
            onSent?.();
        } catch (e) {
            console.error('onSent handler threw', e);
        }
        onClose();
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    // React to global forms status to show success screen
    useNonInitialEffect(() => {
        if (formsStatus === FormsStatus.SendingForms) {
            setSending(true);
        } else if (formsStatus === FormsStatus.SendFormsSuccess) {
            setSending(false);
            setSent(true);
        } else if (formsStatus === FormsStatus.SendFormsError) {
            setSending(false);
            dispatch(showErrorThunk('Error sending form.') as any);
        }
    }, [formsStatus]);

    const closeButton = <MobileCloseButton onClick={sent ? handleDone : onClose} />;

    // Render success content
    if (sent) {
        return (
            <Drawer onClose={handleDone}>
                <div className={styles.resultsPanel}>
                    <div className={styles.header}>
                        <h2 className={styles.title}>Send Form</h2>
                        {closeButton}
                    </div>

                    <div className={styles.containerResults}>
                        <FontAwesomeIcon
                            icon={faCheckCircle}
                            className={styles.successIcon}
                        />
                        <span className={styles.titleResults}>Form(s) Sent</span>
                        <p className={styles.successMessage}>
                            The form(s) has been successfully sent to{' '}
                            <strong className={styles.successCount}>{clientName}</strong>.
                        </p>
                    </div>
                </div>
                <div className={styles.footer}>
                    <Button
                        label="Done"
                        onClick={handleDone}
                        variant="black"
                        size="large"
                        fullWidth={true}
                    />
                </div>
            </Drawer>
        );
    }

    return (
        <Drawer onClose={handleClose}>
            <div className={styles.wrapper}>
                <div className={styles.panel}>
                    <div className={styles.header}>
                        <h3 className={styles.title}>Send Form</h3>
                        {closeButton}
                    </div>

                    {/* Client Information Section */}
                    <div className={styles.sectionClient}>
                        <h4 className={styles.sectionTitle}>Client Information</h4>
                        <div className={styles.clientInfo}>
                            <span className={styles.clientName}>{clientName}</span>
                            <div className={styles.clientContact}>
                                {clientEmail && (
                                    <span className={styles.contactItem}>
                                        <FontAwesomeIcon icon={faEnvelope} />
                                        {clientEmail}
                                    </span>
                                )}
                                {clientPhone && (
                                    <span className={styles.contactItem}>
                                        <FontAwesomeIcon icon={faMobile} />
                                        {formatPhoneNumber(clientPhone || '', marketplace.basics.address.country!.id)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Forms Section */}
                    <div className={styles.section}>
                        <h4 className={styles.sectionTitle}>Forms</h4>
                        <p className={styles.sectionDescription}>Select multiple forms.</p>

                        <div className={styles.formsWrapper}>
                            <SelectMultipleDropdown
                                label="Forms"
                                options={formOptions}
                                showSelectAll={true}
                                value={selectedForms}
                                onChange={(v: any) => setSelectedForms(v || [])}
                                buttonProps={{
                                    variant: 'outlined',
                                    label:
                                        selectedForms.length === 1
                                            ? selectedForms[0].label
                                            : selectedForms.length > 1
                                            ? `${selectedForms.length} Selected`
                                            : 'Select Forms',
                                    fullWidth: true
                                }}
                            />
                        </div>
                    </div>

                    {/* Channel Section */}
                    <Channel
                        selectedChannel={selectedChannel}
                        onChannelChange={setSelectedChannel}
                    />

                    {showMissingAlert && (
                        <div className={styles.noEmailOrPhone}>
                            <AlertMessage
                                icon={faInfoCircle}
                                iconColor="#BAA997"
                                bdColor="#BCB8AE"
                                bgColor="#F8F5F1"
                                className={styles.alert}
                            >
                                <span>
                                    This client does not have a {missingLabel} added. Please{' '}
                                    <a
                                        href="#"
                                        onClick={handleClientProfileLinkClick}
                                    >
                                        add
                                    </a>{' '}
                                    it to send the form(s) {missingSuffix}.
                                </span>
                            </AlertMessage>
                        </div>
                    )}
                </div>

                <div className={styles.footer}>
                    <Button
                        label="Send"
                        variant="green"
                        disabled={selectedForms.length === 0 || sending || showMissingAlert}
                        fullWidth
                        onClick={handleSend}
                    />
                </div>
            </div>
        </Drawer>
    );
};

export default SendForms;
